import qs from 'querystring';
import React, { FC, useState, ReactChild, useEffect, useMemo } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useLazyQuery } from '@apollo/client';
import FormikInput from '@minecraft.formik-input';
import { gqlAccountStatusEnum as ACCOUNT_STATUS } from '@minecraft.graphql-types';
import Label from '@minecraft.label';
import UncontrolledAlert from '@minecraft.uncontrolled-alert';
import { getEnvironment } from '@minecraft.environment';
import { useTranslation } from '@blocs.i18n';
import { getAccessToken } from '@blocs.auth';
import { useFeatureExperiment, FEATURE_FLAGS } from '@blocs.features-experiments';
import { Instance } from '../types';
import getForgotPasswordError from '../../../utils/getForgotPasswordError';
import isLegacyUserAbleToRestorePassword from '../../../utils/isLegacyUserAbleToRestorePassword';
import { StyledForm, FieldWrapper, StyledButton } from './ModalBodyStyles';
import { useValidationMessages } from './constants';
import { PARTIAL_BAU_ACCOUNT_DETAILS } from './query';

const onboardingUrl = getEnvironment()?.ONBOARDING_URL;

type Props = Pick<Instance, 'instance'> & { migratedAccountID: number };

const ForgotPassword: FC<Props> = ({ instance, migratedAccountID }) => {
  const { t } = useTranslation();
  const isLegacyOnboardingDeprecated = useFeatureExperiment(FEATURE_FLAGS.WEB_ONBOARDING_DEPRECATION);
  const validationMessages = useValidationMessages();
  const [username, setUsername] = useState<string>();
  const [errorMessage, setErrorMessage] = useState<ReactChild>();
  const [getBauAccountDetails, { data, error, loading }] = useLazyQuery(PARTIAL_BAU_ACCOUNT_DETAILS);

  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      username: Yup.string().required(validationMessages.EMPTY_FIELD_ERROR),
    });
  }, [validationMessages]);

  const onSubmit = (values) => {
    setUsername(values.username);

    // getBauAccountDetails from useLazyQuery does not return a promise, the typings are wrong
    // see: https://github.com/apollographql/react-apollo/issues/3499
    /* eslint-disable-next-line @typescript-eslint/no-floating-promises */
    getBauAccountDetails({
      variables: {
        input: {
          legacyLogin: values.username,
          legacyInstance: instance.value,
          migratedAccountID,
        },
      },
    });
  };

  useEffect(() => {
    if (error) {
      setErrorMessage(
        getForgotPasswordError(error, isLegacyOnboardingDeprecated, t) || error?.graphQLErrors?.[0]?.message
      );

      return;
    }

    if (data?.partialBauAccountDetails) {
      if (data?.partialBauAccountDetails.accountStatusCode === ACCOUNT_STATUS.ACTIVE) {
        setErrorMessage(validationMessages.ALREADY_CLAIMED);

        return;
      }

      if (!isLegacyUserAbleToRestorePassword(data?.partialBauAccountDetails)) {
        setErrorMessage(validationMessages.UNABLE_TO_RESTORE_ACCOUNT_MSG);

        return;
      }

      const params = qs.stringify({
        username,
        instance: instance.value,
        audienceToken: getAccessToken(),
        skipMainStepRender: true,
      });

      window.location.replace(`${onboardingUrl}/account-recovery?${params}`);
    }
  }, [data, error, isLegacyOnboardingDeprecated, t]);

  return (
    <Formik initialValues={{ username: '' }} validationSchema={validationSchema} onSubmit={onSubmit}>
      {({ isValid }) => {
        return (
          <StyledForm>
            {loading && <p>{t('common:loading.loadingMessage')}</p>}
            {Boolean(errorMessage) && (
              <UncontrolledAlert onClose={(): void => setErrorMessage('')} color="warning">
                {errorMessage}
              </UncontrolledAlert>
            )}
            <p>{t('common:bauForgotPassword.formTitle')}</p>
            <h4>{instance?.item}</h4>
            <FieldWrapper>
              <Label for="username">{t('common:form.label.usernameRequired')}</Label>
              <FormikInput name="username" type="text" />
            </FieldWrapper>
            <StyledButton type="submit" disabled={!isValid || loading}>
              {t('common:button.confirm')}
            </StyledButton>
          </StyledForm>
        );
      }}
    </Formik>
  );
};

export default ForgotPassword;
